import { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import partialRight from 'lodash/partialRight';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { LoadableComponentContext } from '@wix/da-hooks/pkg/useLoadableComponent';
import { getRequiresFallback } from '../../DaEditorWrapper/redux/selectors';
import { getIsFocusRequested, getPublishResult, isBusy as isBusySelector } from '../redux/selectors';
import { CommentEditorSource } from '../redux/types';
import { useEditorDraftUnmountEffects } from './useEditorDraftUnmountEffects';
import { usePublishSuccessCleanup } from './usePublishSuccessCleanup';
import { useEditorDraftState } from './useEditorDraftState';
import { useForceLoadIfNeeded } from './useForceLoadIfNeeded';
import { BiData } from '@wix/da-bi/pkg/events';
/** Provides all the state and effects you'll need for a Comment editor! */ export function useCommentEditor(commentBoxId, commentableItemId, commentableType, { editorSource, relevantComment, autoFocus, initiallyExpanded, onPublishSuccess } = {
    editorSource: CommentEditorSource.Independent
}) {
    const isFocusRequested = useSelector(partialRight(getIsFocusRequested, commentBoxId, autoFocus));
    const logBiEvent = useBiLogger();
    const { triggerLoad } = useContext(LoadableComponentContext);
    const [isExpanded, setIsExpanded] = useState(initiallyExpanded || isFocusRequested || editorSource === CommentEditorSource.Edit);
    // Handle everything for expanding a comment box:
    //  - Logging BI
    //  - Updating the loading context so that our CommentEditor chunk gets loaded
    //  - Updating the local state's isExpanded indicator
    const expandEditor = useCallback(()=>{
        logBiEvent(BiData({
            evid: 183,
            itemid: typeof commentableItemId === 'string' ? parseInt(commentableItemId) : commentableItemId,
            typeid: commentableType
        }));
        triggerLoad();
        setIsExpanded(true);
    }, [
        logBiEvent,
        commentableItemId,
        commentableType,
        triggerLoad
    ]);
    const collapseEditor = useCallback(()=>setIsExpanded(false), []);
    const editorDraftState = useEditorDraftState(commentBoxId);
    useCommentEditorEffects({
        editorId: commentBoxId,
        isFocusRequested,
        expandEditor,
        collapseEditor,
        onPublishSuccess,
        editorSource,
        hasInitialContent: editorSource === CommentEditorSource.Edit || !!editorDraftState,
        autoFocus
    });
    // We need to figure out what the content of the comment box should be on mount. There are two options:
    //  1. A draft that the user started, but left the page
    //  2. The content of an existing comment that the user is editing.
    let initialContent = null;
    if (editorDraftState) {
        initialContent = editorDraftState;
    } else if (editorSource === CommentEditorSource.Edit && relevantComment) {
        initialContent = relevantComment.textContent;
    }
    const [isFallbackEmpty, setIsFallbackEmpty] = useState(!initialContent);
    // Now some values from redux we pass straight through
    const isBusy = useSelector(partialRight(isBusySelector, commentBoxId));
    const shouldUseFallback = useSelector(getRequiresFallback);
    return {
        editorSource,
        isBusy,
        shouldUseFallback,
        isFallbackEmpty,
        setIsFallbackEmpty,
        relevantComment,
        isFocusRequested,
        isExpanded,
        expandEditor,
        collapseEditor,
        initialContent
    };
}
/**
 * Handles all the effects that a comment editor needs to handle.
 *
 * Some of what it handles:
 *  - Draft saving/deleting
 *  - Error handling
 *  - Expand on focus
 *  - Collapse on publish
 *  - and more!
 */ function useCommentEditorEffects({ editorId, isFocusRequested, collapseEditor, expandEditor, onPublishSuccess, editorSource, hasInitialContent, autoFocus }) {
    useEditorDraftUnmountEffects(editorId);
    const publishResult = useSelector(partialRight(getPublishResult, editorId));
    // Make sure we trigger onPublishSuccess if publishResult comes back successful
    // TODO: Run this in a saga somewhere instead?
    usePublishSuccessCleanup(editorId, publishResult, onPublishSuccess);
    useEffect(()=>{
        if (publishResult) {
            collapseEditor();
        }
    }, [
        publishResult,
        collapseEditor
    ]);
    useEffect(()=>{
        if (isFocusRequested) {
            expandEditor();
        }
    }, [
        isFocusRequested,
        expandEditor
    ]);
    useForceLoadIfNeeded(editorSource, isFocusRequested, hasInitialContent, autoFocus);
}
